import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { format, isToday, isYesterday } from "date-fns";
import { 
  Send, 
  Users, 
  MessageCircle, 
  Hash,
  Target,
  ChevronLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPage() {
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [linkedLead, setLinkedLead] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchRooms();
    fetchUsers();
    fetchLeads();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.room_id);
      
      // Poll for new messages every 3 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(selectedRoom.room_id, true);
      }, 3000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [selectedRoom?.room_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchRooms = async () => {
    try {
      const response = await fetch(`${API}/chat/rooms`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setRooms(data.rooms);
        
        // Auto-select team room if exists
        const teamRoom = data.rooms.find(r => r.room_type === "team");
        if (teamRoom && !selectedRoom) {
          setSelectedRoom(teamRoom);
        }
      }
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/leads?limit=50`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads);
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  };

  const fetchMessages = async (roomId, silent = false) => {
    try {
      const response = await fetch(`${API}/chat/rooms/${roomId}/messages`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      if (!silent) {
        console.error("Failed to fetch messages:", error);
      }
    }
  };

  const openTeamChat = async () => {
    try {
      const response = await fetch(`${API}/chat/rooms/team`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const room = await response.json();
        setSelectedRoom(room);
        fetchRooms();
      }
    } catch (error) {
      toast.error("Failed to open team chat");
    }
  };

  const openDMChat = async (userId) => {
    try {
      const response = await fetch(`${API}/chat/rooms/dm/${userId}`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        const room = await response.json();
        setSelectedRoom(room);
        fetchRooms();
      }
    } catch (error) {
      toast.error("Failed to open chat");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/chat/rooms/${selectedRoom.room_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          content: newMessage.trim(),
          lead_id: linkedLead || null
        })
      });

      if (response.ok) {
        setNewMessage("");
        setLinkedLead("");
        fetchMessages(selectedRoom.room_id);
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, "h:mm a");
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, "h:mm a")}`;
    }
    return format(date, "MMM d, h:mm a");
  };

  const getRoomName = (room) => {
    if (room.room_type === "team") return "Team Chat";
    return room.other_user?.name || "Direct Message";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6" data-testid="chat-page">
      {/* Sidebar - Chat list */}
      <Card className={`w-80 flex-shrink-0 flex flex-col ${selectedRoom ? "hidden lg:flex" : "flex"}`}>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Messages
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden flex flex-col">
          {/* Team Chat Button */}
          <Button
            variant="outline"
            className="w-full mb-4 justify-start"
            onClick={openTeamChat}
            data-testid="open-team-chat"
          >
            <Hash className="w-4 h-4 mr-2" />
            Team Chat
            {rooms.find(r => r.room_type === "team")?.unread_count > 0 && (
              <Badge className="ml-auto">{rooms.find(r => r.room_type === "team")?.unread_count}</Badge>
            )}
          </Button>

          {/* DM Section */}
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">
            Direct Messages
          </p>
          <ScrollArea className="flex-1">
            <div className="space-y-1">
              {users.map((user) => {
                const dmRoom = rooms.find(
                  r => r.room_type === "direct" && r.other_user?.name === user.name
                );
                
                return (
                  <button
                    key={user.user_id}
                    onClick={() => openDMChat(user.user_id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-md transition-colors ${
                      selectedRoom?.other_user?.name === user.name
                        ? "bg-primary/10"
                        : "hover:bg-slate-100"
                    }`}
                    data-testid={`dm-user-${user.user_id}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="text-xs">{getInitials(user.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{user.role?.replace("_", " ")}</p>
                    </div>
                    {dmRoom?.unread_count > 0 && (
                      <Badge className="bg-primary">{dmRoom.unread_count}</Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className={`flex-1 flex flex-col ${!selectedRoom ? "hidden lg:flex" : "flex"}`}>
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden"
                  onClick={() => setSelectedRoom(null)}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                {selectedRoom.room_type === "team" ? (
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Hash className="w-5 h-5 text-primary" />
                  </div>
                ) : (
                  <Avatar>
                    <AvatarImage src={selectedRoom.other_user?.picture} />
                    <AvatarFallback>{getInitials(selectedRoom.other_user?.name)}</AvatarFallback>
                  </Avatar>
                )}
                <div>
                  <CardTitle className="font-heading text-lg">{getRoomName(selectedRoom)}</CardTitle>
                  <p className="text-xs text-slate-500">
                    {selectedRoom.room_type === "team" ? "Everyone in your organization" : "Direct message"}
                  </p>
                </div>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full p-4">
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No messages yet</p>
                      <p className="text-sm">Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.message_id} className="flex gap-3" data-testid={`message-${msg.message_id}`}>
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={msg.sender_picture} />
                          <AvatarFallback className="text-xs">{getInitials(msg.sender_name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="font-medium text-sm">{msg.sender_name}</span>
                            <span className="text-xs text-slate-400">{formatMessageTime(msg.created_at)}</span>
                          </div>
                          {msg.lead_name && (
                            <Badge variant="outline" className="text-xs mt-1 mb-1">
                              <Target className="w-3 h-3 mr-1" />
                              Re: {msg.lead_name}
                            </Badge>
                          )}
                          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            </CardContent>

            {/* Message Input */}
            <div className="p-4 border-t">
              {linkedLead && (
                <div className="mb-2">
                  <Badge variant="secondary" className="text-xs">
                    <Target className="w-3 h-3 mr-1" />
                    Linking to: {leads.find(l => l.lead_id === linkedLead)?.business_name}
                    <button 
                      className="ml-2 hover:text-red-500"
                      onClick={() => setLinkedLead("")}
                    >
                      ×
                    </button>
                  </Badge>
                </div>
              )}
              <div className="flex gap-2">
                <Select value={linkedLead || "none"} onValueChange={(v) => setLinkedLead(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-10 px-2" data-testid="link-lead-btn">
                    <Target className="w-4 h-4" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No lead</SelectItem>
                    {leads.map(lead => (
                      <SelectItem key={lead.lead_id} value={lead.lead_id}>
                        {lead.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={!newMessage.trim() || sending}
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm">Choose a team chat or direct message</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
